/**
 * AetherLogin
 * Two-step login: email+password → 2FA OTP from email → dashboard.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useAetherAuth } from './AetherAuthContext';
import { Mail, Lock, Loader2, ArrowLeft, ShieldCheck, KeyRound, CheckCircle2, UserPlus } from 'lucide-react';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Fonts loaded globally via @fontsource in src/index.js (no Google request).
const STYLES = `
  /* Anmeldung im Look der Website (Startseite, Support): Manrope, Kopf links wie der Startseiten-Kopf,
     rechts eine dunkle Glas-Karte mit orangem Hauptknopf. Hintergrund = Lichtszene (GlobalEffects). */
  .ael-container { font-family: Manrope, sans-serif; }
  .ael-seite { position: relative; z-index: 10; min-height: 100vh; width: 100%; max-width: 1240px; margin: 0 auto;
    display: grid; grid-template-columns: minmax(0, 1fr) minmax(360px, 440px); align-items: center; gap: 64px; padding: 112px 32px 64px; }
  .ael-logo { position: absolute; top: 28px; left: 32px; z-index: 20; display: flex; align-items: center; gap: 10px;
    color: #fff; font: 700 16px Syne, sans-serif; transition: opacity .2s; }
  .ael-logo:hover { opacity: .8; }
  .ael-logo img { width: 32px; height: 32px; border-radius: 50%; box-shadow: 0 0 0 2px rgba(255,255,255,.1); }
  .ael-kicker { font: 500 12px 'JetBrains Mono', monospace; letter-spacing: .12em; text-transform: uppercase; color: #F97316; }
  .ael-titel { margin: 18px 0 0; font: 800 clamp(38px, 4.2vw, 60px)/1.02 Manrope, sans-serif; letter-spacing: -.035em; color: #fff; }
  .ael-titel span { color: #8f8f99; }
  .ael-punkte { display: grid; gap: 18px; margin-top: 34px; }
  .ael-punkt b { display: block; font: 800 clamp(22px, 2.3vw, 32px)/1.05 Syne, sans-serif; color: #fff; }
  .ael-punkt b span { color: #F97316; text-shadow: 0 0 22px rgba(249,115,22,.55); }
  .ael-punkt small { display: block; margin-top: 5px; font: 500 15px Manrope, sans-serif; color: rgba(255,255,255,.62); }
  @media (max-width: 1023px) {
    .ael-seite { grid-template-columns: minmax(0, 1fr); gap: 28px; max-width: 520px; padding: 96px 16px 48px; align-content: center; }
    .ael-logo { top: 22px; left: 16px; }
    .ael-titel { font-size: 34px; }
    .ael-punkte { display: none; }
  }
  .ael-glass {
    position: relative;
    background: rgba(14,11,9,.74);
    backdrop-filter: blur(22px) saturate(140%);
    -webkit-backdrop-filter: blur(22px) saturate(140%);
    border: 1px solid rgba(255,255,255,.1);
    border-radius: 20px;
    box-shadow: 0 40px 90px -40px rgba(0,0,0,.95), inset 0 1px 0 rgba(255,255,255,.06);
  }
  .ael-input {
    min-height: 48px;
    background: rgba(255,255,255,.04);
    border: 1px solid rgba(255,255,255,.12);
    border-radius: 12px;
    color: white;
    font: 500 15px Manrope, sans-serif;
    transition: border-color .2s, box-shadow .2s, background .2s;
  }
  .ael-input:focus { outline: none; border-color: rgba(249,115,22,.6); background: rgba(255,255,255,.06); box-shadow: 0 0 0 3px rgba(249,115,22,.15); }
  .ael-input::placeholder { color: rgba(255,255,255,.28); }
  .ael-container .ael-btn {
    min-height: 50px;
    background: #F97316;
    color: #1a0b02;
    border-radius: 12px;
    font: 700 15px Manrope, sans-serif;
    letter-spacing: 0;
    text-transform: none;
    box-shadow: 0 10px 30px -10px rgba(249,115,22,.7);
    transition: background .2s, box-shadow .2s, transform .2s;
  }
  .ael-container .ael-btn:hover:not(:disabled) { background: #fb8a3c; box-shadow: 0 14px 36px -10px rgba(249,115,22,.85); transform: translateY(-1px); }
  .ael-container .ael-btn:disabled { opacity: .45; cursor: not-allowed; }
  /* Google-Knopf (Glas-Variante, Farben inline): ohne orangen Schein */
  .ael-container .ael-btn[aria-hidden="true"] { box-shadow: none; }
  .ael-otp-input {
    width: 48px;
    height: 56px;
    text-align: center;
    font: 600 24px 'JetBrains Mono', monospace;
    background: rgba(255,255,255,.04);
    border: 1px solid rgba(255,255,255,.12);
    border-radius: 12px;
    color: white;
    transition: border-color .2s, box-shadow .2s, background .2s;
  }
  .ael-otp-input:focus { outline: none; border-color: rgba(249,115,22,.6); background: rgba(255,255,255,.06); box-shadow: 0 0 0 3px rgba(249,115,22,.15); }
  /* früher Schreibmaschine in Versalien; jetzt normale Schrift wie auf der Website */
  .ael-container .ael-font-mono { font-family: Manrope, sans-serif; text-transform: none; letter-spacing: 0; font-size: 13px; }
  .ael-container input[type="checkbox"] { accent-color: #F97316; }
`;

/** Inline Google "G" logo as SVG so we don't load anything cross-origin. */
function GoogleGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.83H9v3.46h4.84c-.21 1.13-.84 2.09-1.79 2.73v2.27h2.9c1.7-1.56 2.69-3.86 2.69-6.63z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.27c-.8.54-1.83.86-3.06.86-2.35 0-4.34-1.59-5.05-3.72H.94v2.34A9 9 0 0 0 9 18z"/>
      <path fill="#FBBC05" d="M3.95 10.69A5.42 5.42 0 0 1 3.66 9c0-.59.1-1.16.29-1.69V4.97H.94A9 9 0 0 0 0 9c0 1.45.35 2.83.94 4.03l3.01-2.34z"/>
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.51.46 3.44 1.35l2.58-2.58A9 9 0 0 0 9 0 9 9 0 0 0 .94 4.97l3.01 2.34C4.66 5.18 6.65 3.58 9 3.58z"/>
    </svg>
  );
}

export default function AetherLogin() {
  const { requestOtp, verifyOtp, resendOtp, requestPasswordReset, confirmPasswordReset, loginWithGoogle, register } = useAetherAuth();
  const googleBtnRef = useRef(null);
  // 'credentials' | 'otp' | 'forgot' | 'reset' | 'reset-done' | 'register'
  const [step, setStep] = useState('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [emailDelivered, setEmailDelivered] = useState(true);
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [regName, setRegName] = useState('');
  const [regPassword2, setRegPassword2] = useState('');
  // Google erst nach Klick laden (2-Klick-Lösung): vorher geht keine Anfrage und keine IP an Google
  const [googleLaden, setGoogleLaden] = useState(false);
  const [googleBereit, setGoogleBereit] = useState(false);

  // If user lands here with ?reset_token=... go straight into the reset screen.
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const t = url.searchParams.get('reset_token');
      if (t) {
        setResetToken(t);
        setStep('reset');
      }
    } catch { /* ignore */ }
  }, []);

  // Google Identity Services — only renders the button if a client-id is
  // configured. Stays silent in local dev where the env var isn't set.
  // Das Skript von accounts.google.com wird ERST nach dem ersten Klick auf „Mit Google anmelden“
  // geladen (16.10.2026). Vorher lud schon das Öffnen der Anmeldeseite Google und schickte damit die
  // IP-Adresse jedes Besuchers dorthin (gleiches Problem wie Google-Schriften, LG München I 2022).
  // Nach dem Laden liegt Googles echter Knopf unsichtbar über unserem, der zweite Klick geht an Google.
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || step !== 'credentials' || !googleLaden) return;
    let cancelled = false;

    const renderButton = () => {
      if (cancelled || !window.google || !googleBtnRef.current) return;
      try {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: async (response) => {
            if (!response?.credential) return;
            setError(''); setBusy(true);
            try {
              await loginWithGoogle(response.credential, remember);
              // success — provider sets user, gate re-renders
            } catch (err) {
              setError(err.message || 'Google-Login fehlgeschlagen');
            } finally {
              setBusy(false);
            }
          },
          // Suppress the auto-displayed One-Tap prompt — Google styles it
          // with a white card we can't theme dark, so it clashes badly with
          // the Aether layout. The on-page button is the only entry point.
          auto_select: false,
          use_fedcm_for_prompt: false,
        });
        // Explicit cancel in case GSI already queued a One-Tap show.
        try { window.google.accounts.id.cancel(); } catch { /* ignore */ }
        googleBtnRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          theme: 'filled_black',
          size: 'large',
          shape: 'pill',
          text: 'signin_with',
          width: googleBtnRef.current.offsetWidth || 320,
        });
        setGoogleBereit(true);
      } catch { /* GSI lib not ready yet — caller will retry */ }
    };

    if (window.google?.accounts?.id) {
      renderButton();
    } else if (!document.getElementById('google-gsi-script')) {
      const s = document.createElement('script');
      s.id = 'google-gsi-script';
      s.src = 'https://accounts.google.com/gsi/client';
      s.async = true;
      s.defer = true;
      s.onload = renderButton;
      s.onerror = () => { s.remove(); setGoogleLaden(false); setError('Google-Anmeldung ließ sich nicht laden. Bitte versuch es noch einmal.'); };
      document.head.appendChild(s);
    } else {
      // Script already injected, wait one tick.
      const t = setTimeout(renderButton, 200);
      return () => { cancelled = true; clearTimeout(t); };
    }

    return () => { cancelled = true; };
  }, [step, remember, loginWithGoogle, googleLaden]);

  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !password) {
      setError('Bitte E-Mail und Passwort eingeben');
      return;
    }
    setBusy(true);
    try {
      const data = await requestOtp(email.trim().toLowerCase(), password);
      setEmailDelivered(!!data.email_delivered);
      setStep('otp');
    } catch (err) {
      setError(err.message || 'Login fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    // auto-jump forward
    if (value && index < 5) {
      const next = document.getElementById(`otp-${index + 1}`);
      next?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prev = document.getElementById(`otp-${index - 1}`);
      prev?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').trim().slice(0, 6);
    if (/^\d{1,6}$/.test(pasted)) {
      const arr = pasted.split('');
      const padded = [...arr, ...Array(6 - arr.length).fill('')];
      setOtp(padded);
      const focusIndex = Math.min(pasted.length, 5);
      document.getElementById(`otp-${focusIndex}`)?.focus();
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const code = otp.join('');
    if (code.length !== 6) {
      setError('Bitte alle 6 Ziffern eingeben');
      return;
    }
    setBusy(true);
    try {
      await verifyOtp(email.trim().toLowerCase(), code, remember);
      // success — provider sets user, gate will re-render
    } catch (err) {
      setError(err.message || 'Code ungültig');
      setOtp(['', '', '', '', '', '']);
      document.getElementById('otp-0')?.focus();
    } finally {
      setBusy(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setBusy(true);
    try {
      const data = await resendOtp(email.trim().toLowerCase(), password);
      setEmailDelivered(!!data.email_delivered);
    } catch (err) {
      setError(err.message || 'Resend fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  // Registrierung. Danach KEINE Sitzung — der Nutzer landet auf der Anmeldung und
  // bekommt dort seinen Einmalcode. Wer eine fremde Adresse eintraegt, kommt so nicht
  // hinein, und wir sparen uns eine zweite Bestaetigungsmail.
  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!regName.trim())            { setError('Bitte deinen Namen eingeben'); return; }
    if (!email)                     { setError('Bitte E-Mail eingeben'); return; }
    if (password.length < 8)        { setError('Das Passwort braucht mindestens 8 Zeichen'); return; }
    if (password !== regPassword2)  { setError('Die beiden Passwörter stimmen nicht überein'); return; }
    setBusy(true);
    try {
      await register(email.trim().toLowerCase(), password, regName.trim());
      setStep('credentials');
      setRegPassword2('');
      setError('');
      setEmailDelivered(true);
      // Direkt weiter: Passwort steht noch im Feld, ein Klick auf Anmelden reicht.
    } catch (err) {
      setError(err.message || 'Registrierung fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email) { setError('Bitte E-Mail eingeben'); return; }
    setBusy(true);
    try {
      await requestPasswordReset(email.trim().toLowerCase());
      setForgotSent(true);
    } catch (err) {
      setError(err.message || 'Anfrage fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!resetToken) { setError('Reset-Token fehlt'); return; }
    if (newPassword.length < 8) { setError('Passwort min. 8 Zeichen'); return; }
    if (newPassword !== newPasswordConfirm) { setError('Passwörter stimmen nicht überein'); return; }
    setBusy(true);
    try {
      await confirmPasswordReset(resetToken, newPassword);
      setStep('reset-done');
      // strip ?reset_token from URL so refresh doesn't re-trigger the screen
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('reset_token');
        window.history.replaceState({}, '', url.toString());
      } catch { /* ignore */ }
    } catch (err) {
      setError(err.message || 'Reset fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ael-container relative min-h-screen w-full text-white" data-testid="aether-login">
      <style>{STYLES}</style>

      <a href="/" className="ael-logo" title="Zur Startseite">
        <img src="/logo-badge-192.webp" alt="" />
        DAVIDMEDIA
      </a>

      <div className="ael-seite">
        <div>
          <p className="ael-kicker">David Media Portal</p>
          <h1 className="ael-titel">Dein Konto.<br /><span>Alles an einem Ort.</span></h1>
          <div className="ael-punkte">
            <div className="ael-punkt"><b><span>Deine</span> Werkzeuge</b><small>Produkte und Lizenzschlüssel</small></div>
            <div className="ael-punkt"><b><span>Alle</span> Bestellungen</b><small>Käufe, Abo und Rechnungen</small></div>
            <div className="ael-punkt"><b><span>Ein</span> Login</b><small>mit Code per Mail abgesichert</small></div>
          </div>
        </div>

      <div className="ael-glass w-full p-7 sm:p-9 relative z-20">
        {/* Kopf der Karte: Logo + aktueller Schritt */}
        <div className="text-center mb-8">
          <img src="/logo-badge-192.webp" alt="" className="w-14 h-14 rounded-full mx-auto mb-5 ring-2 ring-white/10" />
          <h2 className="font-manrope text-[28px] font-extrabold tracking-tight leading-none">
            {step === 'credentials' ? 'Anmelden'
              : step === 'otp' ? 'Code eingeben'
              : step === 'register' ? 'Konto anlegen'
              : step === 'forgot' ? 'Passwort vergessen'
              : step === 'reset' ? 'Neues Passwort'
              : 'Erledigt'}
          </h2>
        </div>

        {step === 'credentials' && (
          <form onSubmit={handleCredentialsSubmit} className="space-y-4" data-testid="aether-login-credentials-form">
            <div>
              <label className="ael-font-mono text-[10px] uppercase tracking-widest text-white/40 mb-2 flex items-center gap-2">
                <Mail size={12} /> E-Mail
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                className="ael-input w-full px-4 py-3 text-sm"
                autoFocus
                data-testid="aether-login-email-input"
              />
            </div>
            <div>
              <label className="ael-font-mono text-[10px] uppercase tracking-widest text-white/40 mb-2 flex items-center gap-2">
                <Lock size={12} /> Passwort
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••••"
                className="ael-input w-full px-4 py-3 text-sm"
                data-testid="aether-login-password-input"
              />
            </div>

            <label className="flex items-center gap-2 cursor-pointer select-none pt-2" data-testid="aether-login-remember-toggle">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="w-4 h-4 accent-white/80"
              />
              <span className="ael-font-mono text-[10px] uppercase tracking-widest text-white/50">
                Angemeldet bleiben (7 Tage)
              </span>
            </label>

            {error && (
              <div className="ael-font-mono text-[11px] text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-3" data-testid="aether-login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="ael-btn w-full py-3.5 text-xs font-medium uppercase flex items-center justify-center gap-2"
              data-testid="aether-login-submit-btn"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
              {busy ? 'Wird gesendet' : 'Code anfordern'}
            </button>

            {/* Google Sign-In — always renders the container; if the
                REACT_APP_GOOGLE_CLIENT_ID env-var isn't baked into the
                build we show a visible diagnostic line so the admin sees
                immediately what's wrong instead of a silent empty space. */}
            <div className="pt-2">
              <div className="flex items-center gap-3 mb-3">
                <div className="flex-1 h-px bg-white/5" />
                <span className="ael-font-mono text-[9px] uppercase tracking-[0.3em] text-white/20">oder</span>
                <div className="flex-1 h-px bg-white/5" />
              </div>
              {GOOGLE_CLIENT_ID ? (
                /* Overlay trick: Google's GSI button has its own styling
                   we can't fully theme dark (account-hint pill, white
                   borders, etc.). We render our Aether-styled button on
                   top + the actual GSI button transparent over it — the
                   click still goes to Google, the user sees our theme. */
                <div className="relative w-full">
                  <button
                    type="button"
                    onClick={googleBereit ? undefined : () => { setError(''); setGoogleLaden(true); }}
                    disabled={googleLaden && !googleBereit}
                    className={`ael-btn w-full py-3.5 text-xs font-medium uppercase flex items-center justify-center gap-2${googleBereit ? ' pointer-events-none' : ''}`}
                    style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.9)', border: '1px solid rgba(255,255,255,0.08)', boxShadow: 'none' }}
                    aria-hidden={googleBereit ? 'true' : undefined}
                    title={googleBereit ? undefined : 'Google wird erst bei diesem Klick geladen'}
                  >
                    {googleLaden && !googleBereit ? <Loader2 size={14} className="animate-spin" /> : <GoogleGlyph />}
                    {googleBereit ? 'Weiter mit Google' : googleLaden ? 'Google wird geladen' : 'Mit Google anmelden'}
                  </button>
                  {googleLaden && (
                    <div
                      ref={googleBtnRef}
                      className="absolute inset-0 flex justify-center opacity-0"
                      data-testid="aether-login-google-btn"
                      style={{ colorScheme: 'dark' }}
                    />
                  )}
                </div>
              ) : (
                <div className="ael-font-mono text-[10px] text-amber-400/70 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                  Google-Login nicht konfiguriert · REACT_APP_GOOGLE_CLIENT_ID fehlt im Build
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-4 flex-wrap pt-1">
              <button
                type="button"
                onClick={() => { setError(''); setForgotSent(false); setStep('forgot'); }}
                className="ael-font-mono text-[10px] uppercase tracking-widest text-white/40 hover:text-white/80 transition"
                data-testid="aether-login-forgot-btn"
              >
                Passwort vergessen?
              </button>

              <button
                type="button"
                onClick={() => { setError(''); setPassword(''); setRegPassword2(''); setStep('register'); }}
                className="ael-font-mono text-[10px] uppercase tracking-widest text-white/50 hover:text-white transition"
                data-testid="aether-login-signup-link"
              >
                Noch kein Konto? <span className="text-white/80">Registrieren ›</span>
              </button>
            </div>
          </form>
        )}

        {step === 'register' && (
          <form onSubmit={handleRegisterSubmit} className="space-y-4" data-testid="aether-register-form">
            <div>
              <label className="ael-font-mono text-[10px] uppercase tracking-widest text-white/40 mb-2 flex items-center gap-2">
                <UserPlus size={12} /> Name
              </label>
              <input
                type="text"
                value={regName}
                onChange={(e) => setRegName(e.target.value)}
                placeholder="Vor- und Nachname"
                className="ael-input w-full px-4 py-3 text-sm"
                autoFocus
                data-testid="aether-register-name"
              />
            </div>

            <div>
              <label className="ael-font-mono text-[10px] uppercase tracking-widest text-white/40 mb-2 flex items-center gap-2">
                <Mail size={12} /> E-Mail
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                className="ael-input w-full px-4 py-3 text-sm"
                data-testid="aether-register-email"
              />
            </div>

            <div>
              <label className="ael-font-mono text-[10px] uppercase tracking-widest text-white/40 mb-2 flex items-center gap-2">
                <Lock size={12} /> Passwort
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mindestens 8 Zeichen"
                className="ael-input w-full px-4 py-3 text-sm"
                data-testid="aether-register-password"
              />
            </div>

            <div>
              <label className="ael-font-mono text-[10px] uppercase tracking-widest text-white/40 mb-2 flex items-center gap-2">
                <Lock size={12} /> Passwort wiederholen
              </label>
              <input
                type="password"
                value={regPassword2}
                onChange={(e) => setRegPassword2(e.target.value)}
                placeholder="Nochmal dasselbe"
                className="ael-input w-full px-4 py-3 text-sm"
                data-testid="aether-register-password2"
              />
            </div>

            <p className="text-xs text-white/40 leading-relaxed">
              Nach der Registrierung meldest du dich ganz normal an — den Bestätigungscode
              schicken wir an deine E-Mail.
            </p>

            {error && (
              <div className="ael-font-mono text-[11px] text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-3" data-testid="aether-register-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="ael-btn w-full py-3.5 text-xs font-medium uppercase flex items-center justify-center gap-2"
              data-testid="aether-register-submit"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
              {busy ? 'Wird angelegt' : 'Konto anlegen'}
            </button>

            <div className="text-center pt-2">
              <button
                type="button"
                onClick={() => { setStep('credentials'); setError(''); setRegPassword2(''); }}
                className="ael-font-mono text-[10px] uppercase tracking-widest text-white/40 hover:text-white/80 flex items-center justify-center gap-1 mx-auto transition"
              >
                <ArrowLeft size={11} /> Zurück zur Anmeldung
              </button>
            </div>
          </form>
        )}

        {step === 'forgot' && (
          <form onSubmit={handleForgotSubmit} className="space-y-4" data-testid="aether-login-forgot-form">
            <div>
              <label className="ael-font-mono text-[10px] uppercase tracking-widest text-white/40 mb-2 flex items-center gap-2">
                <Mail size={12} /> E-Mail
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                className="ael-input w-full px-4 py-3 text-sm"
                autoFocus
                disabled={forgotSent}
                data-testid="aether-login-forgot-email"
              />
            </div>

            {forgotSent ? (
              <div className="ael-font-mono text-[11px] text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 flex items-start gap-2">
                <CheckCircle2 size={14} className="mt-0.5 shrink-0" />
                <span>Falls die E-Mail existiert, ist ein Reset-Link unterwegs. Der Link ist 30 Minuten gültig.</span>
              </div>
            ) : (
              <p className="text-xs text-white/40 leading-relaxed">
                Wir senden dir einen Link zum Zurücksetzen deines Passworts. Der Link ist 30 Minuten gültig.
              </p>
            )}

            {error && (
              <div className="ael-font-mono text-[11px] text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                {error}
              </div>
            )}

            {!forgotSent && (
              <button
                type="submit"
                disabled={busy}
                className="ael-btn w-full py-3.5 text-xs font-medium uppercase flex items-center justify-center gap-2"
                data-testid="aether-login-forgot-submit"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                {busy ? 'Wird gesendet' : 'Reset-Link senden'}
              </button>
            )}

            <div className="text-center pt-2">
              <button
                type="button"
                onClick={() => { setStep('credentials'); setError(''); setForgotSent(false); }}
                className="ael-font-mono text-[10px] uppercase tracking-widest text-white/40 hover:text-white/80 flex items-center justify-center gap-1 mx-auto transition"
              >
                <ArrowLeft size={11} /> Zurück zur Anmeldung
              </button>
            </div>
          </form>
        )}

        {step === 'reset' && (
          <form onSubmit={handleResetSubmit} className="space-y-4" data-testid="aether-login-reset-form">
            <div>
              <label className="ael-font-mono text-[10px] uppercase tracking-widest text-white/40 mb-2 flex items-center gap-2">
                <KeyRound size={12} /> Reset-Token
              </label>
              <input
                type="text"
                value={resetToken}
                onChange={(e) => setResetToken(e.target.value)}
                placeholder="Token aus E-Mail-Link"
                className="ael-input w-full px-4 py-3 text-xs"
                readOnly={!!resetToken && resetToken.length > 20}
              />
            </div>
            <div>
              <label className="ael-font-mono text-[10px] uppercase tracking-widest text-white/40 mb-2 flex items-center gap-2">
                <Lock size={12} /> Neues Passwort (min. 8 Zeichen)
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                className="ael-input w-full px-4 py-3 text-sm"
                autoFocus
              />
            </div>
            <div>
              <label className="ael-font-mono text-[10px] uppercase tracking-widest text-white/40 mb-2 flex items-center gap-2">
                <Lock size={12} /> Passwort wiederholen
              </label>
              <input
                type="password"
                value={newPasswordConfirm}
                onChange={(e) => setNewPasswordConfirm(e.target.value)}
                placeholder="••••••••"
                className="ael-input w-full px-4 py-3 text-sm"
              />
            </div>

            {error && (
              <div className="ael-font-mono text-[11px] text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="ael-btn w-full py-3.5 text-xs font-medium uppercase flex items-center justify-center gap-2"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              {busy ? 'Wird gesetzt' : 'Passwort setzen'}
            </button>

            <div className="text-center pt-2">
              <button
                type="button"
                onClick={() => { setStep('credentials'); setError(''); }}
                className="ael-font-mono text-[10px] uppercase tracking-widest text-white/40 hover:text-white/80 flex items-center justify-center gap-1 mx-auto transition"
              >
                <ArrowLeft size={11} /> Abbrechen
              </button>
            </div>
          </form>
        )}

        {step === 'reset-done' && (
          <div className="space-y-5 text-center">
            <CheckCircle2 size={48} className="text-emerald-400 mx-auto" />
            <p className="text-white/80 text-sm">Passwort wurde geändert. Du kannst dich jetzt mit dem neuen Passwort einloggen.</p>
            <button
              type="button"
              onClick={() => { setStep('credentials'); setError(''); setPassword(''); setNewPassword(''); setNewPasswordConfirm(''); setResetToken(''); }}
              className="ael-btn w-full py-3.5 text-xs font-medium uppercase flex items-center justify-center gap-2"
            >
              <ShieldCheck size={14} /> Zur Anmeldung
            </button>
          </div>
        )}

        {step === 'otp' && (
          <form onSubmit={handleOtpSubmit} className="space-y-5" data-testid="aether-login-otp-form">
            <div className="text-center">
              <p className="ael-font-mono text-[11px] uppercase tracking-widest text-white/40 mb-2">
                6-stelliger Code
              </p>
              <p className="text-xs text-white/30">
                Gesendet an <span className="text-white/60 ael-font-mono">{email}</span>
              </p>
              {!emailDelivered && (
                <p className="ael-font-mono text-[10px] text-amber-400/70 mt-3 px-3 py-2 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                  ⚠️ E-Mail konnte nicht gesendet werden — Code wurde in den Server-Logs ausgegeben.
                </p>
              )}
            </div>

            <div className="flex justify-center gap-2" onPaste={handleOtpPaste}>
              {otp.map((digit, i) => (
                <input
                  key={i}
                  id={`otp-${i}`}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                  className="ael-otp-input"
                  autoFocus={i === 0}
                  data-testid={`aether-login-otp-input-${i}`}
                />
              ))}
            </div>

            {error && (
              <div className="ael-font-mono text-[11px] text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-center" data-testid="aether-login-otp-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy || otp.join('').length !== 6}
              className="ael-btn w-full py-3.5 text-xs font-medium uppercase flex items-center justify-center gap-2"
              data-testid="aether-login-verify-btn"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
              {busy ? 'Prüfen' : 'Bestätigen'}
            </button>

            <div className="flex items-center justify-between pt-2">
              <button
                type="button"
                onClick={() => { setStep('credentials'); setOtp(['', '', '', '', '', '']); setError(''); }}
                className="ael-font-mono text-[10px] uppercase tracking-widest text-white/40 hover:text-white/80 flex items-center gap-1 transition"
                data-testid="aether-login-back-btn"
              >
                <ArrowLeft size={11} /> Zurück
              </button>
              <button
                type="button"
                onClick={handleResend}
                disabled={busy}
                className="ael-font-mono text-[10px] uppercase tracking-widest text-white/40 hover:text-white/80 transition disabled:opacity-40"
                data-testid="aether-login-resend-btn"
              >
                Code erneut senden
              </button>
            </div>
          </form>
        )}

        <div className="mt-8 pt-6 border-t border-white/[0.06] text-center">
          <p className="text-xs text-white/35">Geschützter Bereich · David Media</p>
        </div>
      </div>
      </div>
    </div>
  );
}
